// Dropped-item billboards (client render of the E-17 item entities). Each drop is
// a camera-facing sprite of the item icon, bobbing gently; sprites are pooled by
// drop id and disposed when the entity is collected/despawns. Client-only (ENG-1).
import * as THREE from 'three';
import type { ItemDrop } from '../../sim/entities/itemdrop';
import type { ItemIcons } from './textures/itemicons';

export class ItemDropRenderer {
  private readonly group = new THREE.Group();
  private readonly sprites = new Map<number, THREE.Sprite>();
  private lastRevision = -1;

  constructor(scene: THREE.Scene, private readonly icons: ItemIcons) {
    scene.add(this.group);
  }

  update(drops: readonly ItemDrop[], timeMs: number): void {
    // Pooled sprites hold the icon texture in their material, and invalidating
    // an icon DISPOSES that texture — so a re-render has to drop the pool or the
    // sprites keep a disposed map and render blank.
    if (this.icons.revision !== this.lastRevision) {
      this.lastRevision = this.icons.revision;
      this.clear();
    }
    const seen = new Set<number>();
    const bob = Math.sin(timeMs / 300) * 0.06;
    for (const d of drops) {
      seen.add(d.id);
      let sp = this.sprites.get(d.id);
      if (!sp) {
        const mat = new THREE.SpriteMaterial({ map: this.icons.texture(d.stack.item), transparent: true });
        sp = new THREE.Sprite(mat);
        sp.scale.set(0.4, 0.4, 0.4);
        this.sprites.set(d.id, sp);
        this.group.add(sp);
      }
      sp.position.set(d.x, d.y + bob, d.z);
    }
    for (const [id, sp] of this.sprites) {
      if (seen.has(id)) continue;
      this.group.remove(sp);
      (sp.material as THREE.SpriteMaterial).dispose();
      this.sprites.delete(id);
    }
  }

  /** Drop all pooled sprites (dimension switch, DIM-1). */
  clear(): void {
    for (const sp of this.sprites.values()) {
      this.group.remove(sp);
      (sp.material as THREE.SpriteMaterial).dispose();
    }
    this.sprites.clear();
  }
}
